import React from 'react';
import { Box } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

interface BookmarkToggleProps {
  /** Whether the field is bookmarked */
  addToActionItems?: boolean;
  /** Callback when bookmark is toggled */
  onToggle: (e?: React.MouseEvent) => void;
  /** Custom styling */
  sx?: any;
  /** Icon font size, defaults to 'small' */
  iconSize?: 'small' | 'medium' | 'large' | 'inherit';
  /** Accessible label for the toggle button */
  ariaLabel?: string;
}

/**
 * Reusable bookmark toggle component
 * Used throughout sections to mark fields as action items
 */
export const BookmarkToggle: React.FC<BookmarkToggleProps> = ({
  addToActionItems,
  onToggle,
  sx,
  iconSize = 'small',
  ariaLabel = 'Toggle bookmark',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    onToggle(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  };

  return (
    <Box
      role='button'
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={Boolean(addToActionItems)}
      sx={{ cursor: 'pointer', flexShrink: 0, display: 'flex', ...sx }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {addToActionItems ? (
        <BookmarkIcon fontSize={iconSize} sx={{ color: 'primary.main' }} />
      ) : (
        <BookmarkBorderIcon fontSize={iconSize} sx={{ color: 'action.disabled' }} />
      )}
    </Box>
  );
};
